import { useRef, useState } from 'react';
import styled from 'styled-components';
import { colors } from '@ardoq/design-tokens';
import { ColumnResizeHandleModel } from '../types';

const HANDLE_HALF = 3;

const ColHitArea = styled.div`
  pointer-events: auto;
  position: absolute;
  top: 0;
  bottom: 0;
  right: -${HANDLE_HALF}px;
  width: ${HANDLE_HALF * 2}px;
  cursor: col-resize;
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${HANDLE_HALF - 1}px;
    width: 2px;
    background-color: transparent;
    transition: background-color 80ms ease-in-out;
  }
  &:hover::before,
  &.is-dragging::before {
    background-color: ${colors.blue600};
  }
`;

const RowHitArea = styled.div`
  pointer-events: auto;
  position: absolute;
  left: 0;
  right: 0;
  bottom: -${HANDLE_HALF}px;
  height: ${HANDLE_HALF * 2}px;
  cursor: row-resize;
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: ${HANDLE_HALF - 1}px;
    height: 2px;
    background-color: transparent;
    transition: background-color 80ms ease-in-out;
  }
  &:hover::before,
  &.is-dragging::before {
    background-color: ${colors.blue600};
  }
`;

const HandleSlot = styled.div`
  position: relative;
  pointer-events: none;
`;

const useResizeDrag = (
  axis: 'x' | 'y',
  currentSize: number,
  minSize: number,
  onCommit: (size: number) => void
) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{ start: number; startSize: number } | null>(null);

  const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      start: axis === 'x' ? e.clientX : e.clientY,
      startSize: currentSize,
    };
    setIsDragging(true);
  };

  const onPointerUp = (e: React.PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const pos = axis === 'x' ? e.clientX : e.clientY;
    const newSize = Math.max(minSize, drag.startSize + (pos - drag.start));
    if (newSize !== drag.startSize) onCommit(newSize);
    dragRef.current = null;
    setIsDragging(false);
  };

  const onPointerCancel = () => {
    dragRef.current = null;
    setIsDragging(false);
  };

  return { isDragging, onPointerDown, onPointerUp, onPointerCancel };
};

type ColumnResizeHandleProps = ColumnResizeHandleModel & {
  onCommit: (width: number) => void;
  onReset: VoidFunction;
};

const ColumnResizeHandle = ({
  gridColumn,
  gridRowEnd,
  currentWidth,
  minWidth,
  onCommit,
  onReset,
}: ColumnResizeHandleProps) => {
  const { isDragging, onPointerDown, onPointerUp, onPointerCancel } =
    useResizeDrag('x', currentWidth, minWidth, onCommit);
  return (
    <HandleSlot
      style={{
        gridColumn: `${gridColumn} / ${gridColumn + 1}`,
        gridRow: `1 / ${gridRowEnd}`,
      }}
    >
      <ColHitArea
        className={isDragging ? 'is-dragging' : undefined}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onDoubleClick={e => {
          e.stopPropagation();
          onReset();
        }}
      />
    </HandleSlot>
  );
};

type RowResizeHandleProps = {
  gridRow: number;
  gridColumnEnd: number;
  currentHeight: number;
  minHeight: number;
  onCommit: (height: number) => void;
  onReset: VoidFunction;
};

const RowResizeHandle = ({
  gridRow,
  gridColumnEnd,
  currentHeight,
  minHeight,
  onCommit,
  onReset,
}: RowResizeHandleProps) => {
  const { isDragging, onPointerDown, onPointerUp, onPointerCancel } =
    useResizeDrag('y', currentHeight, minHeight, onCommit);
  return (
    <HandleSlot
      style={{
        gridRow: `${gridRow} / ${gridRow + 1}`,
        gridColumn: `1 / ${gridColumnEnd}`,
      }}
    >
      <RowHitArea
        className={isDragging ? 'is-dragging' : undefined}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onDoubleClick={e => {
          e.stopPropagation();
          onReset();
        }}
      />
    </HandleSlot>
  );
};

type RowHeadersResizeHandleProps = {
  currentWidth: number;
  minWidth: number;
  onCommit: (width: number) => void;
  onReset: VoidFunction;
};

const RowHeadersResizeHandle = ({
  currentWidth,
  minWidth,
  onCommit,
  onReset,
}: RowHeadersResizeHandleProps) => {
  const { isDragging, onPointerDown, onPointerUp, onPointerCancel } =
    useResizeDrag('x', currentWidth, minWidth, onCommit);
  return (
    <HandleSlot style={{ gridColumn: '1', gridRow: '1 / -1' }}>
      <ColHitArea
        className={isDragging ? 'is-dragging' : undefined}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onDoubleClick={e => {
          e.stopPropagation();
          onReset();
        }}
      />
    </HandleSlot>
  );
};

export { ColumnResizeHandle, RowResizeHandle, RowHeadersResizeHandle };
